import imageCompression from 'browser-image-compression';

/**
 * Optimize image before upload
 * - Resize to max dimensions
 * - Compress to reduce file size
 * - Convert to JPEG for better compression
 */
export async function optimizeImage(
  file: File,
  options?: {
    maxSizeMB?: number;
    maxWidthOrHeight?: number;
    useWebWorker?: boolean;
  }
): Promise<File> {
  const defaultOptions = {
    maxSizeMB: 2, // Max file size 2MB
    maxWidthOrHeight: 1920, // Max dimension 1920px
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: 0.85,
  };

  const compressionOptions = {
    ...defaultOptions,
    ...options,
  };

  try {
    console.log('[Image Optimization] Original file:', {
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2) + 'MB',
      type: file.type,
    });

    const compressedFile = await imageCompression(file, compressionOptions);

    console.log('[Image Optimization] Compressed file:', {
      name: compressedFile.name,
      size: (compressedFile.size / 1024 / 1024).toFixed(2) + 'MB',
      type: compressedFile.type,
      reduction: (((file.size - compressedFile.size) / file.size) * 100).toFixed(1) + '%',
    });

    return compressedFile;
  } catch (error) {
    console.error('[Image Optimization] Error:', error);
    // If optimization fails, return original file
    return file;
  }
}

/**
 * Optimize image with specific dimensions for different use cases
 */
export async function optimizeImageByType(
  file: File,
  type: 'profile' | 'cover' | 'thumbnail' | 'intro' | 'general'
): Promise<File> {
  const configs = {
    profile: {
      maxSizeMB: 1,
      maxWidthOrHeight: 500, // Profile images are small
    },
    cover: {
      maxSizeMB: 2,
      maxWidthOrHeight: 1600, // Cover images 1600x500
    },
    thumbnail: {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 1280, // Course thumbnails 1280x720
    },
    intro: {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 1000, // Intro images 800x1000
    },
    general: {
      maxSizeMB: 2,
      maxWidthOrHeight: 1920, // General images
    },
  };

  const config = configs[type] || configs.general;
  return optimizeImage(file, config);
}

/**
 * Calculate file hash for duplicate detection
 */
export async function calculateFileHash(file: File): Promise<string> {
  try {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.error('[File Hash] Error calculating hash:', error);
    // Fallback: use file name + size + modified date
    return `${file.name}-${file.size}-${file.lastModified}`;
  }
}

/**
 * Check if file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Get image dimensions
 */
export async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.width,
        height: img.height,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Validate image file
 */
export async function validateImageFile(
  file: File,
  options?: {
    maxSizeMB?: number;
    allowedTypes?: string[];
    minWidth?: number;
    minHeight?: number;
  }
): Promise<{ valid: boolean; error?: string }> {
  const {
    maxSizeMB = 10,
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
    minWidth = 100,
    minHeight = 100,
  } = options || {};

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `ไฟล์ต้องเป็นรูปภาพประเภท: ${allowedTypes.join(', ')}`,
    };
  }

  // Check file size
  const fileSizeMB = file.size / 1024 / 1024;
  if (fileSizeMB > maxSizeMB) {
    return {
      valid: false,
      error: `ไฟล์ใหญ่เกินไป (${fileSizeMB.toFixed(2)}MB) ต้องไม่เกิน ${maxSizeMB}MB`,
    };
  }

  // Check dimensions
  try {
    const dimensions = await getImageDimensions(file);
    if (dimensions.width < minWidth || dimensions.height < minHeight) {
      return {
        valid: false,
        error: `ขนาดรูปภาพต้องไม่น้อยกว่า ${minWidth}x${minHeight}px`,
      };
    }
  } catch (error) {
    return {
      valid: false,
      error: 'ไม่สามารถอ่านข้อมูลรูปภาพได้',
    };
  }

  return { valid: true };
}
